const COMMON_RUNTIME_FIELDS = [
  {
    key: "atr_stop_multiplier",
    label: "ATR Stop Multiplier",
    type: "number",
    min: 0.1,
    step: 0.1,
    description: "Risk distance used for ATR-based stops and trailing protection.",
  },
  {
    key: "risk_reward_ratio",
    label: "Risk/Reward Ratio",
    type: "number",
    min: 0.25,
    step: 0.25,
    description: "Default reward multiple when no explicit account target overrides it.",
  },
  {
    key: "close_on_opposite_signal",
    label: "Close On Opposite Signal",
    type: "boolean",
    description: "Suppress repeat entries and close the current position when the engine flips.",
  },
  {
    key: "reverse_on_opposite_signal",
    label: "Reverse On Flip",
    type: "boolean",
    description: "Open the opposite side immediately after a flip closes the current trade.",
  },
  {
    key: "use_trailing_atr_stop",
    label: "Use ATR Trailing Stop",
    type: "boolean",
    description: "Trail protective stops with ATR while the trade is open.",
  },
];

export const STRATEGY_ENGINES = [
  { value: "TREND_FOLLOWING", label: "Trend Following" },
  { value: "RSI_REVERSAL", label: "RSI Reversal" },
  { value: "SUPERTREND", label: "Supertrend" },
  { value: "KAMA_ATR", label: "Kaufman + ATR" },
  { value: "KAMA_MIDDLE_REGIME", label: "KAMA Middle Regime" },
];

export const STRATEGY_RUNTIME_FIELDS = {
  TREND_FOLLOWING: [
    { key: "ema_fast", label: "EMA Fast", type: "number", min: 1, step: 1 },
    { key: "ema_slow", label: "EMA Slow", type: "number", min: 2, step: 1 },
    { key: "rsi_buy_max", label: "RSI Buy Max", type: "number", min: 1, max: 99, step: 1 },
    { key: "rsi_sell_min", label: "RSI Sell Min", type: "number", min: 1, max: 99, step: 1 },
    { key: "supertrend_required", label: "Require Supertrend", type: "boolean" },
  ],
  RSI_REVERSAL: [
    { key: "oversold", label: "Oversold", type: "number", min: 1, max: 50, step: 1 },
    { key: "overbought", label: "Overbought", type: "number", min: 50, max: 99, step: 1 },
  ],
  SUPERTREND: [
    { key: "bullish_value", label: "Bullish Value", type: "number", step: 1 },
    { key: "bearish_value", label: "Bearish Value", type: "number", step: 1 },
  ],
  KAMA_ATR: [
    { key: "kama_length", label: "KAMA Length", type: "number", min: 2, step: 1 },
    { key: "fast_length", label: "Fast Length", type: "number", min: 1, step: 1 },
    { key: "slow_length", label: "Slow Length", type: "number", min: 2, step: 1 },
    { key: "atr_length", label: "ATR Length", type: "number", min: 1, step: 1 },
    { key: "atr_multiplier", label: "ATR Multiplier", type: "number", min: 0.1, step: 0.1 },
    { key: "enter_on_current_state_when_flat_after_resume", label: "Enter Current State After Resume", type: "boolean" },
  ],
  KAMA_MIDDLE_REGIME: [
    { key: "kama_length", label: "KAMA Length", type: "number", min: 2, step: 1 },
    { key: "fast_length", label: "Fast Length", type: "number", min: 1, step: 1 },
    { key: "slow_length", label: "Slow Length", type: "number", min: 2, step: 1 },
    { key: "atr_length", label: "ATR Length", type: "number", min: 1, step: 1 },
    { key: "require_kama_slope_confirmation", label: "Require KAMA Slope", type: "boolean" },
    { key: "min_body_atr_ratio", label: "Min Body / ATR", type: "number", min: 0, step: 0.05 },
    { key: "cooldown_bars", label: "Cooldown Bars", type: "number", min: 0, step: 1 },
    { key: "enter_on_current_state_when_flat_after_resume", label: "Enter Current State After Resume", type: "boolean" },
  ],
};

export const BUILT_IN_STRATEGY_OPTIONS = [
  {
    value: "TREND_FOLLOWING",
    label: "Trend Following (EMA + RSI + Supertrend)",
    runtime_definition: {
      engine: "TREND_FOLLOWING",
      params: {},
      language: "runtime",
      source: "built_in",
    },
  },
  {
    value: "RSI_REVERSAL",
    label: "RSI Reversal",
    runtime_definition: {
      engine: "RSI_REVERSAL",
      params: {},
      language: "runtime",
      source: "built_in",
    },
  },
  {
    value: "SUPERTREND",
    label: "Supertrend Only",
    runtime_definition: {
      engine: "SUPERTREND",
      params: {},
      language: "runtime",
      source: "built_in",
    },
  },
  {
    value: "KAMA_ATR",
    label: "Kaufman + ATR",
    runtime_definition: {
      engine: "KAMA_ATR",
      params: {},
      language: "runtime",
      source: "built_in",
    },
  },
  {
    value: "KAMA_MIDDLE_REGIME",
    label: "KAMA Middle Regime",
    runtime_definition: {
      engine: "KAMA_MIDDLE_REGIME",
      params: {},
      language: "runtime",
      source: "built_in",
    },
  },
];

export const getDefaultRuntimeParams = (engine) => {
  const common = {
    atr_stop_multiplier: 1.5,
    risk_reward_ratio: 2.0,
    close_on_opposite_signal: true,
    reverse_on_opposite_signal: true,
    use_trailing_atr_stop: true,
  };
  if (engine === "RSI_REVERSAL") {
    return { ...common, oversold: 30, overbought: 70 };
  }
  if (engine === "SUPERTREND") {
    return { ...common, bullish_value: 1, bearish_value: -1 };
  }
  if (engine === "KAMA_ATR") {
    return {
      ...common,
      atr_stop_multiplier: 1.0,
      risk_reward_ratio: 2.5,
      use_trailing_atr_stop: false,
      kama_length: 8,
      fast_length: 2,
      slow_length: 30,
      atr_length: 10,
      atr_multiplier: 1.75,
    };
  }
  if (engine === "KAMA_MIDDLE_REGIME") {
    return {
      ...common,
      atr_stop_multiplier: 1.0,
      risk_reward_ratio: 2.5,
      use_trailing_atr_stop: false,
      kama_length: 8,
      fast_length: 2,
      slow_length: 30,
      atr_length: 10,
      require_kama_slope_confirmation: false,
      min_body_atr_ratio: 0.0,
      cooldown_bars: 0,
    };
  }
  return {
    ...common,
    ema_fast: 9,
    ema_slow: 21,
    rsi_buy_max: 70,
    rsi_sell_min: 30,
    supertrend_required: true,
  };
};

export const buildRuntimeDefinition = (engine, params = {}, language = "python", source = "custom") => ({
  engine,
  params: {
    ...getDefaultRuntimeParams(engine),
    ...(params || {}),
  },
  language,
  source,
});

export const inferStrategyEngine = (name = "", explicitEngine = null) => {
  if (STRATEGY_ENGINES.some((engine) => engine.value === explicitEngine)) {
    return explicitEngine;
  }

  const text = String(name || "").toUpperCase();
  if (text.includes("KAMA MIDDLE") || text.includes("MIDDLE REGIME")) {
    return "KAMA_MIDDLE_REGIME";
  }
  if (text.includes("KAMA") || text.includes("KAUFMAN")) {
    return "KAMA_ATR";
  }
  if (text.includes("MEAN") || text.includes("REVERS")) {
    return "RSI_REVERSAL";
  }
  if (text.includes("SUPER")) {
    return "SUPERTREND";
  }
  return "TREND_FOLLOWING";
};

export const normalizeRuntimeDefinition = (
  runtime,
  fallbackEngine = "TREND_FOLLOWING",
  language = "runtime",
  source = "runtime",
) => {
  const normalizedEngine = inferStrategyEngine(runtime?.name || "", runtime?.engine || fallbackEngine);
  return {
    engine: normalizedEngine,
    params: {
      ...getDefaultRuntimeParams(normalizedEngine),
      ...(runtime?.params || {}),
    },
    language: runtime?.language || language,
    source: runtime?.source || source,
  };
};

export const getRuntimeFields = (engine) => [
  ...(STRATEGY_RUNTIME_FIELDS[engine] || []),
  ...COMMON_RUNTIME_FIELDS,
];

export const coerceRuntimeParamValue = (field, rawValue) => {
  if (field.type === "boolean") {
    return Boolean(rawValue);
  }

  if (field.type === "number") {
    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) {
      return field.min ?? 0;
    }
    return numeric;
  }

  return rawValue;
};

export const normalizeIndicatorDefinition = (indicator) => {
  if (!indicator) {
    return null;
  }

  if (typeof indicator === "string") {
    if (indicator === "EMA") {
      return { type: "EMA", period: 9 };
    }
    if (indicator === "SUPERTREND") {
      return { type: "SUPERTREND", period: 10, multiplier: 3 };
    }
    if (indicator === "RSI") {
      return { type: "RSI", period: 14 };
    }
    if (indicator === "KAMA") {
      return { type: "KAMA", period: 10, fast: 2, slow: 30 };
    }
    if (indicator === "ATR") {
      return { type: "ATR", period: 14 };
    }
    return { type: indicator };
  }

  if (typeof indicator === "object" && indicator.type) {
    return { ...indicator };
  }

  return null;
};

export const normalizeIndicatorsForApi = (indicators = []) =>
  indicators
    .map((indicator) => normalizeIndicatorDefinition(indicator))
    .filter(Boolean);

export const buildStrategyOptions = ({
  visualStrategies = [],
  catalogStrategies = [],
  customStrategies = [],
  includeBuiltIns = true,
  includeNone = false,
} = {}) => {
  const options = [];

  if (includeNone) {
    options.push({
      value: "none",
      label: "No strategy overlay",
      runtime_definition: null,
      source: "none",
    });
  }

  options.push(
    ...(visualStrategies || []).map((strategyItem) => ({
      value: `visual:${strategyItem.id}`,
      label: `${strategyItem.name} (Visual)`,
      runtime_definition: normalizeRuntimeDefinition(
        strategyItem?.runtime_definition,
        strategyItem?.conditions?.strategy_type || inferStrategyEngine(strategyItem?.name),
        "visual",
        "visual",
      ),
      source: "visual",
    })),
  );

  if (includeBuiltIns) {
    options.push(...BUILT_IN_STRATEGY_OPTIONS);
  }

  const catalogOptions = (catalogStrategies || [])
    .map((strategyItem) => {
      const normalizedRuntime = normalizeRuntimeDefinition(
        strategyItem?.runtime_definition,
        inferStrategyEngine(strategyItem?.name),
        "python",
        "catalog",
      );
      const resolvedEngine = inferStrategyEngine(strategyItem?.name, normalizedRuntime?.engine);
      return {
        value: `catalog:${strategyItem.id}`,
        label: strategyItem.name + (strategyItem.description ? ` — ${strategyItem.description}` : ""),
        runtime_definition: normalizedRuntime,
        source: "catalog",
        _resolvedEngine: resolvedEngine,
      };
    })
    .filter((option) => !(includeBuiltIns && ["KAMA_ATR", "KAMA_MIDDLE_REGIME"].includes(option._resolvedEngine)))
    .map(({ _resolvedEngine, ...option }) => option);

  options.push(...catalogOptions);

  options.push(
    ...(customStrategies || []).map((strategyItem) => ({
      value: `custom:${strategyItem.id}`,
      label: `${strategyItem.name} (${strategyItem.language === "pinescript" ? "Pine Script" : "Custom"})`,
      runtime_definition: normalizeRuntimeDefinition(strategyItem?.runtime_definition, inferStrategyEngine(strategyItem?.name), strategyItem?.language || "python", "custom"),
      source: "custom",
    })),
  );

  return options;
};
